import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth, API } from '../App';
import { 
  History, ArrowUpCircle, ArrowDownCircle, Gift, 
  CreditCard, Sparkles, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, Image
} from 'lucide-react';
import { Button } from './ui/button';

const CreditHistory = () => {
  const { token, user } = useAuth();
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchHistory();
    fetchSummary();
  }, [page]);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/credits/history?limit=${limit}&offset=${page * limit}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('Failed to fetch credit history:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await fetch(`${API}/credits/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch credit summary:', err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'usage':
        return <Image className="w-4 h-4 text-orange-500" />;
      case 'purchase':
        return <CreditCard className="w-4 h-4 text-green-500" />;
      case 'signup_bonus':
        return <Sparkles className="w-4 h-4 text-purple-500" />;
      case 'admin_gift':
        return <Gift className="w-4 h-4 text-blue-500" />;
      default:
        return <History className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'usage': return 'Artwork Created';
      case 'purchase': return 'Credits Purchased';
      case 'signup_bonus': return 'Welcome Bonus';
      case 'admin_gift': return 'Admin Gift';
      default: return type;
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="credit-history">
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <TrendingUp className="w-4 h-4" />
              <span>Total Earned</span>
            </div>
            <p className="text-2xl font-bold text-green-500">+{summary.total_added}</p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <TrendingDown className="w-4 h-4" />
              <span>Total Used</span>
            </div>
            <p className="text-2xl font-bold text-orange-500">-{summary.total_used}</p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Image className="w-4 h-4" />
              <span>Artworks</span>
            </div>
            <p className="text-2xl font-bold">{summary.artwork_count}</p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-border rounded-xl p-4"
          >
            <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
              <Sparkles className="w-4 h-4" />
              <span>Current Balance</span>
            </div>
            <p className="text-2xl font-bold text-primary">{summary.current_credits}</p>
          </motion.div>
        </div>
      )}

      {/* History List */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-medium flex items-center gap-2">
            <History className="w-4 h-4" />
            Credit History
          </h3>
          <span className="text-sm text-muted-foreground">{total} transactions</span>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            Loading history...
          </div>
        ) : history.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            <History className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No credit history yet</p>
            <p className="text-sm">Your transactions will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {history.map((item, idx) => (
              <motion.div
                key={item.created_at + idx}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.05 }}
                className="px-4 py-3 flex items-center justify-between hover:bg-muted/30 transition-colors"
                data-testid={`credit-history-item-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                    {getTypeIcon(item.type)}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{getTypeLabel(item.type)}</p>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </div>
                
                <div className="text-right">
                  <p className={`font-mono font-bold ${item.amount > 0 ? 'text-green-500' : 'text-orange-500'}`}>
                    {item.amount > 0 ? '+' : ''}{item.amount}
                  </p>
                  <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage(p => Math.max(0, p - 1))}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page + 1} of {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(p => p + 1)}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditHistory;
